import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';
import { Res, InvoiceInListDTO, DocumentType, SerieInListDTO, InvoiceContentType } from 'core/api';

import { Props } from './ShowInvoicesScreen.Common';
import { InvoiceFacade } from 'pos/facades';
import * as Common from './ShowInvoicesScreen.Common';
import { BarcodeEncoder } from 'pos/services';
import { CountryNames, Events, SessionProvider } from 'core/infrastructure';
import getTableCompanions from '../main/ticket/getTableCompanions';
import * as InvoiceStore from './InvoiceStore';
import { Message, useOverlayExecutor } from 'core/ui-next/modal';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { BarcodeReader, SvgIcon, Toaster } from 'core/ui-next';
import { useClickWithFeedback } from 'pos/ui';
import { VStack, HStack, Separator, TableScrollArea, Box, Text, Table, Button, TableCellProps, Menu, Portal } from '@chakra-ui/react';
import { z } from 'zod';
import { CheckBoxMenuItemDef } from 'core/ui-next/controls/CheckBoxMenu';
import { Close } from 'core/img';
import { zBusinessDay, zInt } from 'core/infrastructure-next';
import * as ChakraUtils from 'core/ui-next/chakra-utils';


const stateSchema = z.object({
	criteria: z.string(),
	mode: z.enum(['BusinessDay', 'SerieAndNumber']),
	from: zBusinessDay(),
	to: zBusinessDay(),
	serie: z.string(),
	number: zInt({ nonNegative: true }),
});
type FormState = z.infer<typeof stateSchema>;


export default function WebPosShowInvoices(props: Props) {

	//Props
	const { businessDay, initialAllSeries, cancel, close } = props;
	const { execute } = useOverlayExecutor();

	//State
	const [criteria, setCriteria] = React.useState<string>('');
	const [selection, setSelection] = React.useState<Nullable<Common.Selection>>(null);
	const [allSeries, setAllSeries] = React.useState<SerieInListDTO[]>(initialAllSeries);
	const [serie, setSerie] = React.useState<SerieInListDTO>(initialAllSeries[0]);
	const [number, setNumber] = React.useState<number>(initialAllSeries[0].NextNumber - 1);
	const [mode, setMode] = React.useState<'BusinessDay' | 'SerieAndNumber'>('BusinessDay');
	const [invoices, setInvoices] = React.useState<Common.InvoiceInListModel[]>([]);
	const [selectedInvoice, setSelectedInvoice] = React.useState<Nullable<Common.InvoiceInListModel>>(null);
	const [selectedColumn, setSelectedColumn] = React.useState<Nullable<ChakraUtils.ColumnDef<Common.InvoiceInListModel>>>(null);
	const [columnSortDirection, setColumnSortDirection] = React.useState<Nullable<ChakraUtils.SortDirection>>(null);


	// Ref con la selección "viva" para que reload no dependa de `selection` y
	// no re-cree la callback en cada cambio (equivalente a leer this.state en la clase).
	const selectionRef = React.useRef(selection);
	React.useEffect(() => { selectionRef.current = selection; });


	const searchInvoices = React.useCallback(async (): Promise<InvoiceInListDTO[]> => {
		const success = await formRef.current!.getValue();
		if (!success.ok)
			return [];

		if (success.value.mode === 'BusinessDay')
			return await InvoiceFacade.getBetweenBusinessDays(success.value.from, success.value.to);

		const found = await InvoiceFacade.getBySerieAndNumber(success.value.serie, success.value.number);
		if (found.Id === 0) {
			Toaster.warn(Res.Msg.MSG_CANNOT_FIND_INVOICE_WITH_SERIAL_AND_NUMBER());
			return [];
		}

		return [found];
	}, []);


	const reload = React.useCallback(async () => {
		const result = await Common.updateInvoices(await searchInvoices(), selectionRef.current);
		setInvoices(result.invoices);
		setFilteredInvoices(result.invoices);
		setSelection(result.selection);
	}, [searchInvoices]);

	// Suscribe eventos de dominio y dispara la carga inicial (equivalente al
	// antiguo ShowInvoices.onComponentDidMount).
	Common.useInvoiceReload(reload);


	const onBarcodeRead = React.useCallback(async (code: string) => {

		const idResult = await BarcodeEncoder.tryDecodeId(BarcodeEncoder.EntityType.Invoice, code);
		if (!idResult.ok) {
			await Message.show(BarcodeEncoder.isQRCode(code)
				? Res.Msg.MSG_QR_IS_NOT_A_VALID_INVOICE_QR(code)
				: Res.Msg.MSG_BARCODE_IS_NOT_A_VALID_INVOICE_BARCODE(code));
			return;
		}

		const invoice = await InvoiceFacade.getById(idResult.value);
		if (!invoice || invoice.Id === 0) {
			await Message.show(Res.Msg.MSG_INVOICE_NOT_FOUND());
			return;
		}

		const { SerieName, Number } = invoice.SerialNumber;
		setMode('SerieAndNumber');
		setSerie(initialAllSeries.find(x => x.Name === SerieName)!);
		setNumber(Number);
		setCriteria('');

		formRef.current?.setValue('criteria', '');
		formRef.current?.setValue('serie', SerieName);
		formRef.current?.setValue('number', Number);
		await reload();
	}, [initialAllSeries, reload]);



	const formRef = React.useRef<FormRef<FormState>>(null);
	const { Form, Input, CheckBoxMenu, DateInput, ComboBox, IntInput } = useControlsFor<FormState>();

	const initialState: FormState = {
		criteria: criteria,
		mode: mode,
		from: businessDay,
		to: businessDay,
		serie: serie.Name,
		number: number
	};


	// Limpiamos el criteria de busqueda del formulario
	const onClear = React.useCallback(async () => {
		setCriteria('');
		formRef.current?.setValue('criteria', '');

		await reload();
	}, [reload, setCriteria]);

	// Estado con las facturas visibles en la tabla; se actualiza al escribir en el buscador.
	const [filteredInvoices, setFilteredInvoices] = React.useState<Common.InvoiceInListModel[]>(invoices);
	const columnDefs = buildColumnDefs(filteredInvoices); // A lo mejor pasar un boolean para indicar si incluye ServiceChargeColumns;

	const onSearch = React.useCallback((criteria: string) => {
		const term = (criteria ?? '').trim().toLowerCase();

		if (term === '') {
			setFilteredInvoices(invoices);
			return;
		}

		const result = invoices.filter(invoice => {
			const fields = [
				UI.Format.asDate(invoice.BusinessDay),
				invoice.SerieAndNumber,
				invoice.Pos,
				invoice.SaleCenter,
				invoice.SaleLocation,
				invoice.User,
				UI.Format.asPrice(invoice.GrossAmount),
				UI.Format.asPrice(invoice.TaxAmount),
				UI.Format.asPrice(invoice.NetAmount),
				UI.Format.asPrice(invoice.TipAmount),
				UI.Format.asPrice(invoice.ServiceChargeGrossAmount),
				UI.Format.asPrice(invoice.ServiceChargeTaxAmount),
				UI.Format.asPrice(invoice.ServiceChargeNetAmount),
				UI.Format.asDateAndTime(invoice.CreationDate),
				DocumentType.toString(invoice.Type),
				invoice.PaymentMethods,
				invoice.TicketId,
				invoice.CustomerFiscalName,
				invoice.CustomerCif,
				CountryNames.getName(invoice.CustomerCountryCode),
				invoice.CustomerAccountCode,
				invoice.CustomerStreet
			];

			return fields.some(value => (value ?? '').toString().toLowerCase().includes(term));
		});

		setFilteredInvoices(result);
	}, [invoices]);


	const onSelectedInvoice = React.useCallback(async (invoice: Common.InvoiceInListModel) => {
		setSelectedInvoice(invoice);
		setSelection(await Common.select(invoice.Id ?? null));
	}, []);


	// Suscripción única a los cambios de facturas para refrescar las series (equivalente al
	// this.on('pos:invoice:changed') que la clase registraba una sola vez en el mount).
	// Events.on devuelve su propia función de baja, que usamos como cleanup del efecto.
	React.useEffect(() => {
		return Events.on('pos:invoice:changed', async () => {
			// Recargo las series por si se han emitido nuevas facturas. Así tengo actualizados
			// los últimos números de cada serie y al seleccionar en el combo puedo mostrar la
			// última factura emitida en esa serie.
			setAllSeries(await InvoiceFacade.getSeries());
		});
	}, []);


	React.useEffect(() => {
		const disposeReader = BarcodeReader.start(onBarcodeRead);

		// Pintamos la línea seleccionada nada más cargar la pantalla
		setTimeout(() => {
			const itemToSelect = filteredInvoices.find(x => x.Id === selection?.invoice.Id);
			if (itemToSelect) {
				// Seleccionamos el item seleccionado en la tabla.
				setSelectedInvoice(itemToSelect);
			}
		});

		return () => {
			disposeReader();
		};
	}, [onBarcodeRead, filteredInvoices, selection]);


	// Cuando cambiamos su estado de forma programática (p.ej. al marcar "Todos" desmarcamos el resto),
	// incrementamos esta key para forzar el remount y que se pinten los checks acordes al estado actual.
	const [checkModeMenuKey, setCheckModeMenuKey] = React.useState<number>(0);
	const remountCheckModeMenu = React.useCallback(() => setCheckModeMenuKey(k => k + 1), []);

	const onModeChange = React.useCallback(async (value: string, checked: boolean) => {
		if (value === 'BusinessDay' && checked)
			setMode('BusinessDay');

		if (value === 'SerieAndNumber' && checked) {
			setMode('SerieAndNumber');
		}
		remountCheckModeMenu();

		// Limpiamos el criterio de búsqueda para que no afecte a la búsqueda realizada en el modo anterior.
		setCriteria('');
		formRef.current?.setValue('criteria', '');

		formRef.current!.setValue('mode', value as 'BusinessDay' | 'SerieAndNumber');
		await reload();
	}, [remountCheckModeMenu, reload, setCriteria]);


	const onSerieChange = React.useCallback(async (name: string | null) => {
		if (name === null) return;

		const serie = allSeries.find(s => s.Name === name);
		if (!serie) return;

		setSerie(serie);
		setNumber(serie.NextNumber - 1);

		formRef.current!.setValue('number', serie.NextNumber - 1);
		await reload();
	}, [allSeries, reload]);


	// No se usa useForceUpdate para forzar el remount del CheckBoxMenu de columnas,
	// ya que al cambiar el estado de showAllColumns o checkedColumns no se actualizan bien las columnas del componente de chakra en la UI.
	const [checkMenuColumnsKey, setCheckMenuColumnsKey] = React.useState<number>(0);
	const remountColumnsCheckMenu = React.useCallback(() => setCheckMenuColumnsKey(k => k + 1), []);

	// Visibilidad de columnas persistida en localStorage. La leemos una única vez al montar
	// para sembrar el estado inicial; si no hay nada guardado (primera vez) usamos los valores
	// por defecto de siempre (todas las columnas visibles).
	const persistedColumnsVisibility = React.useMemo(() => ChakraUtils.getColumnsVisibility('SHOW_INVOICES_COLUMNS_VISIBILITY_KEY'), []);

	// Estado de qué columnas están visibles en la tabla.
	// - `showAllColumns` = true  → se muestran todas las columnas y los checks individuales están desmarcados.
	// - `showAllColumns` = false → se muestran solo las columnas cuyo check individual está marcado.
	const [showAllColumns, setShowAllColumns] = React.useState<boolean>(persistedColumnsVisibility?.showAllColumns ?? true);
	const [checkedColumns, setCheckedColumns] = React.useState<Record<ChakraUtils.ColumnKey<Common.InvoiceInListModel>, boolean>>(() =>
		persistedColumnsVisibility?.checkedColumns
		?? columnDefs.reduce((acc, k) => { acc[k.key] = false; return acc; }, {} as Record<ChakraUtils.ColumnKey<Common.InvoiceInListModel>, boolean>)
	);

	// Cada vez que cambia la visibilidad de columnas (por interacción con el CheckBoxMenu),
	// la guardamos en localStorage para recuperarla en la siguiente apertura de la pantalla.
	React.useEffect(() => {
		ChakraUtils.saveColumnsVisibility({ showAllColumns, checkedColumns }, 'SHOW_INVOICES_COLUMNS_VISIBILITY_KEY');
	}, [showAllColumns, checkedColumns]);

	const clearCheckedColumns = React.useCallback(() => {
		const checkedColumns = columnDefs.reduce((acc, k) => { acc[k.key] = false; return acc; }, {} as Record<ChakraUtils.ColumnKey<Common.InvoiceInListModel>, boolean>);
		setCheckedColumns(checkedColumns);
	}, [columnDefs]);

	// Conjunto de columnas actualmente visibles.
	// - En modo SerieAndNumber se muestran siempre todas las columnas por defecto, sin alterar la
	//   preferencia guardada en localStorage (se recupera tal cual al volver a BusinessDay).
	// - En modo BusinessDay: si "Todos" está marcado se muestran todas, en caso contrario solo las
	//   que tengan su check individual activo.
	const visibleColumns = React.useMemo<Set<ChakraUtils.ColumnKey<Common.InvoiceInListModel>>>(() => {

		const allColumnsKeys = columnDefs.map(c => c.key);

		if (mode !== 'BusinessDay')
			return new Set<ChakraUtils.ColumnKey<Common.InvoiceInListModel>>(allColumnsKeys);

		const checkedColumnsKeys = allColumnsKeys.filter(k => checkedColumns[k]);
		return new Set<ChakraUtils.ColumnKey<Common.InvoiceInListModel>>(showAllColumns ? allColumnsKeys : checkedColumnsKeys);
	}, [columnDefs, showAllColumns, checkedColumns, mode]);


	// Handler para el check "Todos": al marcarlo, se muestran todas las columnas y se desmarcan los demás checks.
	// Al desmarcarlo, se mantiene el conjunto actual de checks individuales.
	const onAllCheckedChange = React.useCallback((checked: boolean) => {
		if (checked) {
			setShowAllColumns(true);
			clearCheckedColumns();
			remountColumnsCheckMenu();
		} else {
			setShowAllColumns(false);
		}
	}, [clearCheckedColumns, remountColumnsCheckMenu]);

	// Handler para cada check de columna: si se marca uno cuando "Todos" estaba activo,
	// se desmarca "Todos" para pasar a modo "solo las columnas marcadas".
	const onColumnCheckedChange = React.useCallback((column: ChakraUtils.ColumnKey<Common.InvoiceInListModel>, checked: boolean) => {
		setCheckedColumns(prev => ({ ...prev, [column]: checked }));
		if (checked && showAllColumns) {
			setShowAllColumns(false);
			remountColumnsCheckMenu();
		}
	}, [showAllColumns, remountColumnsCheckMenu]);



	const reopen = React.useCallback(async () => {
		await execute({
			action: async () => {
				const result = await Common.reopenInvoice(selection);
				if (result.ok)
					close(result.value);
			}
		});
	}, [close, execute, selection]);


	const refund = React.useCallback(async () => {
		await execute({
			action: async () => {
				await Common.createTotalRefund(selection);
			}
		});
	}, [execute, selection]);


	const updateTableCompanions = React.useCallback(async () => {
		if (selection === null)
			return;

		if (selection.invoice.ContentType === InvoiceContentType.DeliveryNotes) {
			await Message.show(Res.Msg.MSG_CANNOT_SET_TABLE_COMPANIONS_ON_INVOICE_FROM_DELIVERY_NOTE());
			return;
		}

		const result = await getTableCompanions({
			FriendlyName: '',
			TableCompanions: selection.invoice.TableCompanions,
			Location: {
				SaleCenterName: '',
				SaleLocationName: ''
			}
		}, 9999);

		if (!result.ok)
			return;

		await execute({
			action: async () => {
				await InvoiceStore.setTableCompanions(selection!.invoice.Id, result.value);
			}
		});
	}, [execute, selection]);


	const partialRefund = React.useCallback(async () => {
		await execute({
			action: async () => {
				await Common.createPartialRefund(selection);
			}
		});
	}, [execute, selection]);


	const onSortBy = (column: ChakraUtils.ColumnDef<Common.InvoiceInListModel>) => {
		if (!column.compare)
			return;

		const currentSortDirection = selectedColumn?.key === column.key ? columnSortDirection : null;
		const nextDirection: ChakraUtils.SortDirection = currentSortDirection === 'asc' ? 'desc' : 'asc';

		setSelectedColumn(column);
		setColumnSortDirection(nextDirection);
		setFilteredInvoices(ChakraUtils.sortRows(filteredInvoices, column, nextDirection));
	};



	const visibleDefs = columnDefs.filter(c => visibleColumns.has(c.key));

	const printInvoiceClickProps = useClickWithFeedback(() => Common.printInvoice(selection));
	const changePaymentClickProps = useClickWithFeedback(() => Common.changePayment(selection));
	const partialRefundClickProps = useClickWithFeedback(() => partialRefund());
	const refundClickProps = useClickWithFeedback(() => refund());
	const reopenClickProps = useClickWithFeedback(() => reopen());
	const convertToStandardClickProps = useClickWithFeedback(async () => { await Common.convertToStandard(selection); await reload(); });
	const sendInvoiceClickProps = useClickWithFeedback(() => Common.sendEmail(selection));
	const updateTableCompanionsClickProps = useClickWithFeedback(() => updateTableCompanions());
	const printCouponsClickProps = useClickWithFeedback(() => Common.printCoupons(selection));
	const assignOrUpdateCustomerClickProps = useClickWithFeedback(() => Common.assignOrUpdateCustomer(selection));
	const setTipsClickProps = useClickWithFeedback(() => Common.setTips(selection));
	const printTicketGiftClickProps = useClickWithFeedback(() => Common.printTicketGift(selection));

	const searchButtonClickProps = useClickWithFeedback(async () => await reload());


	const isRefund = selection?.invoice.IsRefund;
	const isBasic = selection?.invoice.Type === DocumentType.BasicInvoice || selection?.invoice.Type === DocumentType.BasicRefund;
	const allowPrintTicketGift = SessionProvider.allowPrintTicketGift() && !isRefund;


	return <Common.ShowInvoices cancel={cancel} content={
		<HStack gap={'6'} paddingTop={'6'} paddingX={'6'} alignItems={'flex-start'} overflow={'hidden'} height={'full'}>
			<VStack w={'stretch'} height={'full'} gap={'9'} overflow={'hidden'}>
				<Form ref={formRef} initialValue={initialState} schema={stateSchema} style={{ 'width': '100%', 'height': '100%' }}>
					<VStack w={'full'} height={'full'} gap={'9'} overflow={'hidden'}>
						<HStack width={'full'} gap={'9'}>
							<VStack width={'41'} gap={'2'}>
								<Box width={'full'} display={'flex'} justifyContent={'start'} alignItems={'end'}><Text fontSize={'sm'} fontWeight={'semibold'}>{Res.Web.SHOW_INVOICES_FILTER_RESULT_PLACEHOLDER()}</Text></Box>
								<CheckBoxMenu
									key={checkModeMenuKey}
									items={filterCheckMenuItems(onModeChange, mode)}
									icon={<SvgIcon src={Images.ChevronDown} width={'5'} height={'5'} />}
								/>
							</VStack>
							{mode === 'SerieAndNumber' &&
								(<VStack gap={'2'}>
									<HStack gap={'2'}>
										<Box minW={'35'} width={'35'} minH={'6'} display={'flex'} justifyContent={'start'} alignItems={'end'}><Text fontSize={'sm'} fontWeight={'semibold'}>{Res.Web.SHOW_INVOICES_SERIE_LBL()}</Text></Box>
										<Box minW={'35'} width={'35'} minH={'6'} display={'flex'} justifyContent={'start'} alignItems={'end'}><Text fontSize={'sm'} fontWeight={'semibold'}>{Res.Web.SHOW_INVOICES_NUMBER_LBL()}</Text></Box>
										<Box minW={'22'} width={'22'}><></></Box>
									</HStack>
									<HStack gap={'2'}>
										<Box minW={'35'} width={'35'}>
											<ComboBox
												linkTo={'serie'}
												placeholder={Res.Web.SHOW_INVOICES_SERIAL_SERIE_PLACEHOLDER()}
												options={allSeries.map(s => s.Name)}
												onWatch={async (serieName) => { await onSerieChange(serieName); }}
											/>
										</Box>
										<Box minW={'35'} width={'35'}>
											<IntInput
												linkTo={'number'}
												placeholder={Res.Web.SHOW_INVOICES_SERIAL_NUMBER_PLACEHOLDER()}
											/>
										</Box>
										<Box minW={'22'} width={'22'} display={'flex'} justifyContent={'end'} alignItems={'end'}>
											<Button backgroundColor={'secondary'} color={'white'} {...searchButtonClickProps} display={'flex'} justifyContent={'center'} alignItems={'center'} gap={'2'} maxW={'full'}>
												<SvgIcon src={Images.Search} width={'4'} height={'4'} cursor={'pointer'} />
												<Text>{Res.Web.SHOW_INVOICES_SEARCH_BTN()}</Text>
											</Button>
										</Box>
									</HStack>
								</VStack>)}
							{mode === 'BusinessDay' &&
								(<VStack gap={'2'}>
									<HStack gap={'2'}>
										<Box minW={'6'} minH={'6'} ><></></Box>
										<Box minW={'34'} width={'34'} display={'flex'} justifyContent={'start'} alignItems={'end'}><Text fontSize={'sm'} fontWeight={'semibold'}>{Res.Web.SHOW_INVOICES_FROM_BUSINESS_DAY_LBL()}</Text></Box>
										<Box minW={'2'} minH={'6'}><></></Box>
										<Box minW={'34'} width={'34'} display={'flex'} justifyContent={'start'} alignItems={'end'}><Text fontSize={'sm'} fontWeight={'semibold'}>{Res.Web.SHOW_INVOICES_TO_BUSINESS_DAY_LBL()}</Text></Box>
									</HStack>
									<HStack gap={'2'}>
										<Box minW={'6'} minH={'6'} ><SvgIcon src={Images.Calendar} width={'6'} height={'6'} /></Box>
										<Box minW={'34'} width={'34'}><DateInput linkTo={'from'} onWatch={async () => await reload()} /></Box>
										<Box minW={'2'} display={'flex'} minH={'full'} justifyContent={'center'} alignItems={'center'}><Text fontSize={'sm'} fontWeight={'semibold'}>{' - '}</Text></Box>
										<Box minW={'34'} width={'34'}><DateInput linkTo={'to'} onWatch={async () => await reload()} /></Box>
									</HStack>
								</VStack>
								)}
						</HStack>
						<Separator w={'full'} orientation='horizontal' size={'xs'} color={'neutral.300'} />
						<VStack width={'full'} gap={'4'} height={'full'} minH={'0'}>
							<HStack width={'full'} gap={'9'} justifyContent={'space-between'} alignItems={'end'}>
								<HStack width={'82.5'}>
									<Box minW={'4'} minH={'4'}><SvgIcon src={Images.SortByDesc} width={'4'} height={'4'} /></Box>
									<Box width={'full'}><Text fontSize={'2xs'}>{Res.Web.SHOW_INVOICES_SORT_COLUMNS_PLACEHOLDER()}</Text></Box>
								</HStack>
								<Box width={'62.5'}>
									{mode === 'BusinessDay' && (
										<Input
											linkTo={'criteria'}
											placeholder={Res.Web.SHOW_INVOICES_SEARCH_PLACEHOLDER()}
											onWatch={(value) => onSearch(value ?? '')}
											startElement={<SvgIcon src={Images.Search} width={'4'} height={'4'} cursor={'pointer'} />}
											endElement={<SvgIcon src={Close} width={'4'} height={'4'} cursor={'pointer'} onClick={onClear} />}
											borderRadius={'4xl'}
											borderColor={'neutral.600'}
											height={'10'}
										/>)}
								</Box>
								<HStack width={'49'}>
									{mode === 'BusinessDay' && (
										<>
											<SvgIcon src={Images.Columns} width={'6'} height={'6'} />
											<CheckBoxMenu
												key={checkMenuColumnsKey}
												items={columnCheckMenuItems(
													onAllCheckedChange,
													onColumnCheckedChange,
													checkedColumns,
													showAllColumns
												)}
												icon={<SvgIcon src={Images.ChevronDown} width={'5'} height={'5'} />}
											/>
										</>
									)}

								</HStack>
							</HStack>
							<HStack overflow={'auto'} minW={'full'} maxW={'stretch'} height={'full'} minH={'0'} maxH={'stretch'} alignItems={'flex-start'}>
								{ShowInvoiceTable({
									invoices: filteredInvoices,
									columnsDefs: visibleDefs,
									onColumnSort: onSortBy,
									selectedInvoice,
									onSelectedInvoice,
									selectedColumn,
									sortDirection: columnSortDirection
								})}
							</HStack>
						</VStack>
					</VStack>
				</Form>
			</VStack >
			<VStack height={'full'}>
				{selection === null || filteredInvoices.length === 0 || visibleColumns.size === 0
					? (
						<VStack justifyContent={'center'} alignItems={'center'} minW={'95'} height={'full'}>
							<SvgIcon src={Images.RecentSales} width={'16'} height={'16'} />
							<Text>{Res.Web.SHOW_INVOICES_SELECTION_INVOICE_TO_PREVIEW_LBL()}</Text>
						</VStack>)
					: (<VStack minW={'95'} height={'full'}>
						<HStack gap={'0'} alignItems={'flex-start'} width={'full'} justifyContent={'space-evenly'}>
							<VStack width={'13'} gap={'1'} {...printInvoiceClickProps} cursor={'pointer'}>
								<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
									<SvgIcon src={Images.PrintInvoice} width={'6'} height={'6'} />
								</Box>
								<Box paddingX={'2'}>
									<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.SHOW_INVOICES_PRINT_INVOICE_BTN()}</Text>
								</Box>
							</VStack>
							{!isRefund && (
								<VStack width={'13'} gap={'1'} {...changePaymentClickProps} cursor={'pointer'}>
									<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
										<SvgIcon src={Images.InvoiceChangePayment} width={'6'} height={'6'} />
									</Box>
									<Box paddingX={'2'}>
										<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.SHOW_INVOICES_CHANGE_PAYMENT()}</Text>
									</Box>
								</VStack>)
							}
							{!isRefund && (
								<VStack width={'13'} gap={'1'} {...partialRefundClickProps} cursor={'pointer'}>
									<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
										<SvgIcon src={Images.InvoicePartialRefund} width={'6'} height={'6'} />
									</Box>
									<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.SHOW_INVOICES_CREATE_PARTIAL_REFUND_BTN()}</Text>
								</VStack>)
							}
							{!isRefund && (
								<VStack width={'13'} gap={'1'} {...refundClickProps} cursor={'pointer'}>
									<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
										<SvgIcon src={Images.InvoiceRefund} width={'6'} height={'6'} />
									</Box>
									<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.SHOW_INVOICES_CREATE_REFUND_BTN()}</Text>
								</VStack>)
							}
							{!isRefund && (
								<VStack width={'13'} gap={'1'} {...reopenClickProps} cursor={'pointer'}>
									<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
										<SvgIcon src={Images.InvoiceReopen} width={'6'} height={'6'} />
									</Box>
									<Box paddingX={'2'}>
										<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.SHOW_INVOICES_REOPEN_INVOICE_BTN()}</Text>
									</Box>
								</VStack>)
							}
							{isBasic && !isRefund && (
								<VStack width={'13'} gap={'1'} {...convertToStandardClickProps} cursor={'pointer'}>
									<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
										<SvgIcon src={Images.InvoiceCreate} width={'6'} height={'6'} />
									</Box>
									<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.SHOW_INVOICES_CREATE_INVOICE_BTN()}</Text>
								</VStack>)
							}
							<Separator orientation='vertical' height={'stretch'} size={'xs'} border={'1px solid'} borderColor={'neutral.300'} marginX={'1'} />
							<Menu.Root>
								<Menu.Trigger padding={'0'} height={'stretch'} _hover={{ background: 'none' }} border={'none'} alignItems={'flex-start'} asChild>
									<Button variant="outline" size="sm">
										<VStack height={'full'}>
											<Box padding={'2'}>
												<SvgIcon src={Images.JustPointMore} width={'6'} height={'6'} />
											</Box>
											<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Common.MORE_BTN()}</Text>
										</VStack>
									</Button>
								</Menu.Trigger>
								<Portal>
									<Menu.Positioner>
										<Menu.Content>
											<Menu.Item value={'sendInvoice'} {...sendInvoiceClickProps} cursor={'pointer'}>{Res.Web.SHOW_INVOICES_SEND_INVOICE_BTN()}</Menu.Item>
											<Menu.Item value={'tableCompanions'} {...updateTableCompanionsClickProps} cursor={'pointer'}>{Res.Web.SHOW_INVOICES_TABLE_COMPANIONS_BTN()}</Menu.Item>
											<Menu.Item value={'printCoupons'} {...printCouponsClickProps} cursor={'pointer'}>{Res.Web.SHOW_INVOICES_PRINT_COUPONS_BTN()}</Menu.Item>
											<Menu.Item value={'assignOrUpdateCustomer'} {...assignOrUpdateCustomerClickProps} cursor={'pointer'}>{selection.invoice.Customer.Id === 0 ? Res.Web.SHOW_INVOICES_ASSIGN_CUSTOMER_BTN() : Res.Web.SHOW_INVOICES_ASSIGN_UPDATE_BTN()}</Menu.Item>
											{!isRefund && (
												<Menu.Item value={'setTips'} {...setTipsClickProps} cursor={'pointer'}>{Res.Web.SHOW_INVOICES_CHANGE_TIP_BTN()}</Menu.Item>
											)}
											{allowPrintTicketGift && (
												<Menu.Item value={'printTicketGift'} {...printTicketGiftClickProps} cursor={'pointer'}>{Res.Web.SHOW_INVOICES_PRINT_TICKET_GIFT_BTN()}</Menu.Item>
											)}
										</Menu.Content>
									</Menu.Positioner>
								</Portal>
							</Menu.Root>
						</HStack>
						<VStack justifyContent={'flex-start'} alignItems={'flex-start'} overflowY={'auto'} overflowX={'hidden'}>
							<UI.TemplatePrintPreview preview={selection.preview} />
						</VStack>
					</VStack>
					)}
			</VStack >
		</HStack >
	} />;
}


function filterCheckMenuItems(onModeChange: (value: string, checked: boolean) => void, mode: 'BusinessDay' | 'SerieAndNumber'): CheckBoxMenuItemDef[] {

	const checkMenuItems: CheckBoxMenuItemDef[] = [
		{ value: 'BusinessDay', title: Res.Web.SHOW_INVOICES_BUSINESS_DAY_BTN(), checked: mode === 'BusinessDay', onCheckedChange: (checked) => onModeChange('BusinessDay', checked) },
		{ value: 'SerieAndNumber', title: Res.Web.SHOW_INVOICES_SERIAL_NUMBER_BTN(), checked: mode === 'SerieAndNumber', onCheckedChange: (checked) => onModeChange('SerieAndNumber', checked) },
	];

	return checkMenuItems;
}

function columnCheckMenuItems(onAllCheckedChange: (checked: boolean) => void, onColumnCheckedChange: (column: ChakraUtils.ColumnKey<Common.InvoiceInListModel>, checked: boolean) => void, checkedColumns: Record<string, boolean>, showAllColumns: boolean): CheckBoxMenuItemDef[] {
	const checkMenuItems: CheckBoxMenuItemDef[] = [
		{ value: 'All', title: Res.Common.CHECK_BOX_MENU_ALL_OPTION_LBL(), checked: showAllColumns, onCheckedChange: onAllCheckedChange },
		{ value: 'BusinessDay', title: Res.Web.SHOW_INVOICES_BUSINESS_DAY_CLM(), checked: checkedColumns.BusinessDay, onCheckedChange: (c) => onColumnCheckedChange('BusinessDay', c) },
		{ value: 'SerieAndNumber', title: Res.Web.SHOW_INVOICES_SERIAL_NUMBER_CLM(), checked: checkedColumns.SerieAndNumber, onCheckedChange: (c) => onColumnCheckedChange('SerieAndNumber', c) },
		{ value: 'Pos', title: Res.Web.SHOW_INVOICES_POS_CLM(), checked: checkedColumns.Pos, onCheckedChange: (c) => onColumnCheckedChange('Pos', c) },
		{ value: 'SaleCenter', title: Res.Web.SHOW_INVOICES_SALE_CENTER_CLM(), checked: checkedColumns.SaleCenter, onCheckedChange: (c) => onColumnCheckedChange('SaleCenter', c) },
		{ value: 'SaleLocation', title: Res.Web.SHOW_INVOICES_SALE_LOCATION_CLM(), checked: checkedColumns.SaleLocation, onCheckedChange: (c) => onColumnCheckedChange('SaleLocation', c) },
		{ value: 'User', title: Res.Web.SHOW_INVOICES_USER_CLM(), checked: checkedColumns.User, onCheckedChange: (c) => onColumnCheckedChange('User', c) },
		{ value: 'GrossAmount', title: Res.Web.SHOW_INVOICES_GROSS_AMOUNT_CLM(), checked: checkedColumns.GrossAmount, onCheckedChange: (c) => onColumnCheckedChange('GrossAmount', c) },
		{ value: 'TaxAmount', title: Res.Web.SHOW_INVOICES_TAX_AMOUNT_CLM(), checked: checkedColumns.TaxAmount, onCheckedChange: (c) => onColumnCheckedChange('TaxAmount', c) },
		{ value: 'NetAmount', title: Res.Web.SHOW_INVOICES_NET_AMOUNT_CLM(), checked: checkedColumns.NetAmount, onCheckedChange: (c) => onColumnCheckedChange('NetAmount', c) },
		{ value: 'TipAmount', title: Res.Web.SHOW_INVOICES_TIP_AMOUNT_CLM(), checked: checkedColumns.TipAmount, onCheckedChange: (c) => onColumnCheckedChange('TipAmount', c) },
		{ value: 'ServiceChargeGrossAmount', title: Res.Web.SHOW_INVOICES_SERVICE_CHARGE_GROSS_AMOUNT_CLM(), checked: checkedColumns.ServiceChargeGrossAmount, onCheckedChange: (c) => onColumnCheckedChange('ServiceChargeGrossAmount', c) },
		{ value: 'ServiceChargeTaxAmount', title: Res.Web.SHOW_INVOICES_SERVICE_CHARGE_TAX_AMOUNT_CLM(), checked: checkedColumns.ServiceChargeTaxAmount, onCheckedChange: (c) => onColumnCheckedChange('ServiceChargeTaxAmount', c) },
		{ value: 'ServiceChargeNetAmount', title: Res.Web.SHOW_INVOICES_SERVICE_CHARGE_NET_AMOUNT_CLM(), checked: checkedColumns.ServiceChargeNetAmount, onCheckedChange: (c) => onColumnCheckedChange('ServiceChargeNetAmount', c) },
		{ value: 'CreationDate', title: Res.Web.SHOW_INVOICES_CREATION_DATE_CLM(), checked: checkedColumns.CreationDate, onCheckedChange: (c) => onColumnCheckedChange('CreationDate', c) },
		{ value: 'Type', title: Res.Web.SHOW_INVOICES_TYPE_CLM(), checked: checkedColumns.Type, onCheckedChange: (c) => onColumnCheckedChange('Type', c) },
		{ value: 'PaymentMethods', title: Res.Web.SHOW_INVOICES_PAYMENT_METHODS_CLM(), checked: checkedColumns.PaymentMethods, onCheckedChange: (c) => onColumnCheckedChange('PaymentMethods', c) },
		{ value: 'TicketId', title: Res.Web.SHOW_INVOICES_TICKET_ID_CLM(), checked: checkedColumns.TicketId, onCheckedChange: (c) => onColumnCheckedChange('TicketId', c) },
		{ value: 'CustomerFiscalName', title: Res.Web.SHOW_INVOICES_CUSTOMER_FISCAL_NAME_CLM(), checked: checkedColumns.CustomerFiscalName, onCheckedChange: (c) => onColumnCheckedChange('CustomerFiscalName', c) },
		{ value: 'CustomerCif', title: Res.Web.SHOW_INVOICES_CUSTOMER_CIF_CLM(), checked: checkedColumns.CustomerCif, onCheckedChange: (c) => onColumnCheckedChange('CustomerCif', c) },
		{ value: 'CustomerCountryCode', title: Res.Web.SHOW_INVOICES_CUSTOMER_COUNTRY_CODE_CLM(), checked: checkedColumns.CustomerCountryCode, onCheckedChange: (c) => onColumnCheckedChange('CustomerCountryCode', c) },
		{ value: 'CustomerAccountCode', title: Res.Web.SHOW_INVOICES_CUSTOMER_ACCOUNT_CODE_CLM(), checked: checkedColumns.CustomerAccountCode, onCheckedChange: (c) => onColumnCheckedChange('CustomerAccountCode', c) },
		{ value: 'CustomerStreet', title: Res.Web.SHOW_INVOICES_CUSTOMER_ADDRESS_CLM(), checked: checkedColumns.CustomerStreet, onCheckedChange: (c) => onColumnCheckedChange('CustomerStreet', c) },
	];

	return checkMenuItems;
}


function buildColumnDefs(invoices: Common.InvoiceInListModel[]): ChakraUtils.ColumnDef<Common.InvoiceInListModel>[] {

	const columns: ChakraUtils.ColumnDef<Common.InvoiceInListModel>[] = [
		{ key: 'BusinessDay', header: Res.Web.SHOW_INVOICES_BUSINESS_DAY_CLM(), render: i => UI.Format.asDate(i.BusinessDay), compare: (a: Date, b: Date) => new Date(a).getTime() - new Date(b).getTime() },
		{ key: 'SerieAndNumber', header: Res.Web.SHOW_INVOICES_SERIAL_NUMBER_CLM(), render: i => i.SerieAndNumber, compare: (a: string, b: string) => a.localeCompare(b) },
		{ key: 'Pos', header: Res.Web.SHOW_INVOICES_POS_CLM(), render: i => i.Pos, compare: (a: string, b: string) => a.localeCompare(b) },
		{ key: 'SaleCenter', header: Res.Web.SHOW_INVOICES_SALE_CENTER_CLM(), render: i => i.SaleCenter, compare: (a: string, b: string) => a.localeCompare(b) },
		{ key: 'SaleLocation', header: Res.Web.SHOW_INVOICES_SALE_LOCATION_CLM(), render: i => i.SaleLocation, compare: (a: string, b: string) => a.localeCompare(b) },
		{ key: 'User', header: Res.Web.SHOW_INVOICES_USER_CLM(), render: i => i.User, compare: (a: string, b: string) => a.localeCompare(b) },
		{ key: 'GrossAmount', header: Res.Web.SHOW_INVOICES_GROSS_AMOUNT_CLM(), render: i => UI.Format.asMoney(i.GrossAmount), compare: (a: number, b: number) => a - b, textAlign: 'end' },
		{ key: 'TaxAmount', header: Res.Web.SHOW_INVOICES_TAX_AMOUNT_CLM(), render: i => UI.Format.asMoney(i.TaxAmount), compare: (a: number, b: number) => a - b, textAlign: 'end' },
		{ key: 'NetAmount', header: Res.Web.SHOW_INVOICES_NET_AMOUNT_CLM(), render: i => UI.Format.asMoney(i.NetAmount), compare: (a: number, b: number) => a - b, textAlign: 'end' },
		{ key: 'TipAmount', header: Res.Web.SHOW_INVOICES_TIP_AMOUNT_CLM(), render: i => UI.Format.asMoney(i.TipAmount), compare: (a: number, b: number) => a - b, textAlign: 'end' },
	];

	// Si tengo alguna factura con recargo, añado las columnas correspondientes
	if (invoices.some(i => i.ServiceChargeGrossAmount)) {
		columns.push(
			{ key: 'ServiceChargeGrossAmount', header: Res.Web.SHOW_INVOICES_SERVICE_CHARGE_GROSS_AMOUNT_CLM(), render: i => UI.Format.asMoney(i.ServiceChargeGrossAmount), compare: (a: number, b: number) => a - b, textAlign: 'end' },
			{ key: 'ServiceChargeTaxAmount', header: Res.Web.SHOW_INVOICES_SERVICE_CHARGE_TAX_AMOUNT_CLM(), render: i => UI.Format.asMoney(i.ServiceChargeTaxAmount), compare: (a: number, b: number) => a - b, textAlign: 'end' },
			{ key: 'ServiceChargeNetAmount', header: Res.Web.SHOW_INVOICES_SERVICE_CHARGE_NET_AMOUNT_CLM(), render: i => UI.Format.asMoney(i.ServiceChargeNetAmount), compare: (a: number, b: number) => a - b, textAlign: 'end' },
		);
	}

	columns.push(
		{ key: 'CreationDate', header: Res.Web.SHOW_INVOICES_CREATION_DATE_CLM(), render: i => UI.Format.asDateAndTime(i.CreationDate), compare: (a: Date, b: Date) => new Date(a).getTime() - new Date(b).getTime() },
		{ key: 'Type', header: Res.Web.SHOW_INVOICES_TYPE_CLM(), render: i => DocumentType.toString(i.Type), compare: (a: DocumentType, b: DocumentType) => DocumentType.toString(a).localeCompare(DocumentType.toString(b)) },
		{ key: 'PaymentMethods', header: Res.Web.SHOW_INVOICES_PAYMENT_METHODS_CLM(), render: i => i.PaymentMethods, compare: (a: string, b: string) => a.localeCompare(b) },
		{ key: 'TicketId', header: Res.Web.SHOW_INVOICES_TICKET_ID_CLM(), render: i => i.TicketId, compare: (a: Nullable<number>, b: Nullable<number>) => (a ?? 0) - (b ?? 0) },
		{ key: 'CustomerFiscalName', header: Res.Web.SHOW_INVOICES_CUSTOMER_FISCAL_NAME_CLM(), render: i => i.CustomerFiscalName, compare: (a: string, b: string) => a.localeCompare(b) },
		{ key: 'CustomerCif', header: Res.Web.SHOW_INVOICES_CUSTOMER_CIF_CLM(), render: i => i.CustomerCif, compare: (a: string, b: string) => a.localeCompare(b) },
		{ key: 'CustomerCountryCode', header: Res.Web.SHOW_INVOICES_CUSTOMER_COUNTRY_CODE_CLM(), render: i => CountryNames.getName(i.CustomerCountryCode), compare: (a: string, b: string) => a.localeCompare(b) },
		{ key: 'CustomerAccountCode', header: Res.Web.SHOW_INVOICES_CUSTOMER_ACCOUNT_CODE_CLM(), render: i => i.CustomerAccountCode, compare: (a: string, b: string) => a.localeCompare(b) },
		{ key: 'CustomerStreet', header: Res.Web.SHOW_INVOICES_CUSTOMER_ADDRESS_CLM(), render: i => i.CustomerStreet, compare: (a: string, b: string) => a.localeCompare(b) }
	);

	return columns;
}

// Idea, la tabla recive un listado de facturas, las definciones de columnas y un callback para cuando se haga click en una columna para ordenar.
function ShowInvoiceTable(params: {
	invoices: Common.InvoiceInListModel[],
	columnsDefs: ChakraUtils.ColumnDef<Common.InvoiceInListModel>[],
	onColumnSort: (column: ChakraUtils.ColumnDef<Common.InvoiceInListModel>) => void,
	selectedInvoice: Nullable<Common.InvoiceInListModel>,
	onSelectedInvoice: (invoice: Common.InvoiceInListModel) => void,
	selectedColumn: Nullable<ChakraUtils.ColumnDef<Common.InvoiceInListModel>>,
	sortDirection: Nullable<ChakraUtils.SortDirection>,

}): React.ReactNode {

	const { invoices, columnsDefs, onColumnSort, selectedInvoice, onSelectedInvoice, selectedColumn, sortDirection } = params;

	// Necesitamos saber si la columna BusinessDay está visible para calcular el left de la columna SerieAndNumber,
	// ya que si BusinessDay está visible, SerieAndNumber debe ir a la izquierda 24(sizes.ts), y si no, a la izquierda 0.
	// Al ser BussinesDay y SerieAndNumber columnas stickies.
	const serieAndNumberLeft = columnsDefs.some(c => c.key === 'BusinessDay') ? '24' : '0';


	return <TableScrollArea maxWidth={'stretch'} minWidth={'full'} maxHeight={'stretch'} rounded={'md'}>
		<Table.Root
			size={'sm'}
			striped
			stickyHeader
			interactive
			stickyColumns
		>
			<Table.Header>
				<Table.Row>
					{columnsDefs.map(col => (
						<Table.ColumnHeader key={col.key} textAlign={col.textAlign} onClick={() => onColumnSort(col)} cursor={'pointer'} {...(col.key === 'BusinessDay' ? { 'data-sticky': 'end', 'left': '0', 'minWidth': '27' } : col.key === 'SerieAndNumber' ? { 'data-sticky': 'end', 'left': serieAndNumberLeft } : undefined)}>
							<HStack gap={'0.5'} justifyContent={'flex-start'}>
								<Text fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{col.header}</Text>
								{selectedColumn?.key === col.key && <SvgIcon src={sortDirection === 'asc' ? Images.SortByDesc : Images.SortByAsc} width={'4'} height={'4'} />}
							</HStack>
						</Table.ColumnHeader>
					))}
				</Table.Row>
			</Table.Header>
			<Table.Body>
				{(invoices.length === 0 || columnsDefs.length === 0) && (
					<Table.Row>
						<Table.Cell colSpan={Math.max(columnsDefs.length, 1)} textAlign={'center'}>
							<Text fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{Res.Web.SHOW_INVOICES_NO_INVOICES_LBL()}</Text>
						</Table.Cell>
					</Table.Row>
				)}
				{invoices.length > 0 && invoices.map((invoice) => {
					const cellBackGround: TableCellProps['bg'] = invoice.Id === selectedInvoice?.Id ? { base: '{colors.blue.200}', _dark: '{colors.blue.500}' } : undefined;
					return (
						<Table.Row key={invoice.Id} onClick={() => onSelectedInvoice(invoice)} cursor={'pointer'}>
							{columnsDefs.map(col => (
								<Table.Cell background={cellBackGround} textAlign={col.textAlign} key={col.key} {...(col.key === 'BusinessDay' ? { 'data-sticky': 'end', 'left': '0', 'minWidth': '27' } : col.key === 'SerieAndNumber' ? { 'data-sticky': 'end', 'left': serieAndNumberLeft } : undefined)}>
									<Text fontSize={'sm'}>{col.render(invoice)}</Text>
								</Table.Cell>
							))}
						</Table.Row>);
				})}
			</Table.Body>
		</Table.Root>
	</TableScrollArea >;
} 
